import { useState, useEffect } from 'react';
import {
  Database,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Loader2,
  Clock,
  Settings,
  BookOpen,
  Briefcase,
  Calendar,
  Download,
  Upload,
  FileSpreadsheet,
  Trash2
} from 'lucide-react';
import { authenticatedFetch } from '@/lib/auth';
import { useConfirm } from '../ui';

interface ESCOSyncStatus {
  id: string;
  esco_version: string;
  synced_at: string;
  skills_count: number;
  occupations_count: number;
  relations_count: number;
  sync_duration_ms: number;
  status: string;
  error_message?: string;
  // Granulær progress (v5.15.3) — oppdateres throttled av backend under sync
  current_step?: string | null;
  items_processed?: number;
  total_items?: number;
  progress_updated_at?: string | null;
  // Siste vellykkede sync (egen rad fra DB) — viser når databasen FAKTISK ble oppdatert
  last_successful_sync?: {
    synced_at: string;
    esco_version: string;
    skills_count: number;
    occupations_count: number;
    sync_duration_ms: number;
    sync_type: string;
  } | null;
}

// Norske fase-labels for current_step
const STEP_LABELS: Record<string, string> = {
  starting: 'Starter synkronisering',
  fetching_skills: 'Henter ferdigheter fra EU ESCO API',
  fetching_occupations: 'Henter yrkesroller fra EU ESCO API',
  importing_skills: 'Importerer ferdigheter til database',
  importing_occupations: 'Importerer yrkesroller til database',
  importing_relations: 'Importerer yrke-ferdighet-relasjoner',
  finalizing: 'Fullfører — beregner relasjoner',
};

interface ESCOSyncSettings {
  sync_enabled: boolean;
  sync_frequency_days: number;
  last_check_at: string | null;
}

interface ESCOStatistics {
  skills_count: number;
  occupations_count: number;
}

export default function ESCOSettings() {
  const { confirm } = useConfirm();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // CSV Import state
  const [importing, setImporting] = useState<'skills' | 'occupations' | 'relations' | null>(null);
  const [clearing, setClearing] = useState(false);

  // Pre-cache state (relations via API)
  const [precacheState, setPrecacheState] = useState<{
    running: boolean;
    index: number;
    total: number;
    fetched: number;
    skipped: number;
    errors: number;
    relationsAdded: number;
    finishedAt: string | null;
    result: any;
    error: string | null;
  } | null>(null);
  const [precachePolling, setPrecachePolling] = useState(false);

  const [syncStatus, setSyncStatus] = useState<ESCOSyncStatus | null>(null);
  const [settings, setSettings] = useState<ESCOSyncSettings>({
    sync_enabled: true,
    sync_frequency_days: 7,
    last_check_at: null
  });
  const [statistics, setStatistics] = useState<ESCOStatistics>({
    skills_count: 0,
    occupations_count: 0
  });
  const [syncHistory, setSyncHistory] = useState<ESCOSyncStatus[]>([]);

  // Fetch ESCO sync status - returns the status for polling
  const fetchStatus = async (): Promise<ESCOSyncStatus | null> => {
    try {
      const response = await authenticatedFetch('/api/esco/local/sync/status');
      if (response.ok) {
        const data = await response.json();
        setSyncStatus(data.last_sync);
        setSettings(data.settings);
        setStatistics(data.statistics);
        return data.last_sync;
      }
    } catch (err) {
      console.error('Failed to fetch ESCO status:', err);
    }
    return null;
  };

  // Fetch sync history
  const fetchHistory = async () => {
    try {
      const response = await authenticatedFetch('/api/esco/local/sync/history?limit=5');
      if (response.ok) {
        const data = await response.json();
        setSyncHistory(data.history || []);
      }
    } catch (err) {
      console.error('Failed to fetch ESCO history:', err);
    }
  };

  // Track sync progress message
  const [syncProgress, setSyncProgress] = useState('');

  // Hjelpefunksjon: returner mm:ss eller "X min Y sek" basert på sekunder
  const formatElapsed = (seconds: number): string => {
    if (seconds < 60) return `${Math.floor(seconds)} sek`;
    const min = Math.floor(seconds / 60);
    const sec = Math.floor(seconds % 60);
    return sec === 0 ? `${min} min` : `${min} min ${sec} sek`;
  };

  // Bygg progress-melding fra DB-data. Prefereres over tidsbaserte estimat
  // siden backend nå skriver granulær fremdrift (current_step + items_processed/total).
  // Fallback til tidsbasert estimat hvis kolonnene ikke er populert ennå.
  const progressMessageFor = (status: ESCOSyncStatus | null | undefined): string => {
    if (!status) return 'Synkronisering pågår...';
    const elapsed = (Date.now() - new Date(status.synced_at).getTime()) / 1000;
    const elapsedTxt = formatElapsed(elapsed);
    // Foretrukket: granulær fase + telling
    if (status.current_step) {
      const stepLabel = STEP_LABELS[status.current_step] || status.current_step;
      if (status.total_items && status.total_items > 0) {
        const pct = Math.min(100, Math.round((status.items_processed || 0) / status.total_items * 100));
        return `${stepLabel}: ${(status.items_processed || 0).toLocaleString('nb-NO')} / ${status.total_items.toLocaleString('nb-NO')} (${pct}%) · ${elapsedTxt}`;
      }
      return `${stepLabel}... (${elapsedTxt})`;
    }
    // Fallback (tidsbasert)
    if (elapsed < 60) return `Laster ned ferdigheter fra EU ESCO API... (${elapsedTxt})`;
    if (elapsed < 300) return `Laster ned yrkesroller... (${elapsedTxt})`;
    return `Importerer data til database... (${elapsedTxt})`;
  };

  // Poll-funksjon (extrahert slik at den kan kalles ved mount hvis sync allerede kjører)
  const startPolling = (initialMessage?: string) => {
    setSyncing(true);
    setSyncProgress(initialMessage || 'Synkronisering pågår...');
    const pollInterval = setInterval(async () => {
      const currentStatus = await fetchStatus();
      // Bruk full status-objekt for granulær fremdrift (current_step + items_processed/total)
      if (currentStatus) {
        setSyncProgress(progressMessageFor(currentStatus));
      }
      if (currentStatus && currentStatus.status !== 'running') {
        clearInterval(pollInterval);
        setSyncing(false);
        setSyncProgress('');
        await fetchHistory();
        if (currentStatus.status === 'completed') {
          setSuccess(`Synkronisering fullført! ${currentStatus.skills_count?.toLocaleString() || 0} ferdigheter og ${currentStatus.occupations_count?.toLocaleString() || 0} yrkesroller importert på ${formatDuration(currentStatus.sync_duration_ms)}.`);
        } else if (currentStatus.status === 'failed') {
          setError(`Synkronisering feilet: ${currentStatus.error_message || 'Ukjent feil'}`);
        }
      }
    }, 5000);
    // Safety timeout (15 min — backend cleanup tar over hvis det er en orphan)
    setTimeout(() => {
      clearInterval(pollInterval);
      setSyncing((wasSyncing) => {
        if (wasSyncing) {
          setSyncProgress('');
          fetchStatus();
          fetchHistory();
        }
        return false;
      });
    }, 900000);
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const [status] = await Promise.all([fetchStatus(), fetchHistory()]);
      setLoading(false);
      // Hvis backend har en aktiv 'running'-sync ved sidens lasting, gjenoppta polling.
      // Backend rydder hengende rader >60 min eldre automatisk, så det vi ser her
      // er enten en sync som faktisk pågår nå eller en nylig dødd som snart blir
      // markert som mislykket.
      if (status && status.status === 'running') {
        const ageMinutes = (Date.now() - new Date(status.synced_at).getTime()) / 60000;
        if (ageMinutes < 60) {
          // Sett umiddelbart riktig melding basert på faktisk DB-state.
          // Inkluderer current_step + items_processed/total fra ny progress-tracker.
          startPolling(progressMessageFor(status));
        }
      }
    };
    loadData();
  }, []);

  // Trigger manual sync. Synken kjører i bakgrunnen på server — bruker kan
  // trygt forlate siden og komme tilbake for å se status. Frontend gjenopptar
  // polling automatisk ved sidens lasting hvis sync fortsatt er aktiv.
  const handleManualSync = async () => {
    setError('');
    setSuccess('');
    try {
      const response = await authenticatedFetch('/api/esco/local/sync/trigger', {
        method: 'POST'
      });
      if (response.ok) {
        startPolling('Synkronisering startet. Laster ned data fra EU ESCO API...');
      } else {
        const data = await response.json();
        setError(data.error || 'Kunne ikke starte synkronisering');
      }
    } catch (err: any) {
      setError(err.message || 'Nettverksfeil');
    }
  };

  // Save settings
  const handleSaveSettings = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await authenticatedFetch('/api/esco/local/sync/settings', {
        method: 'POST',
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        setSuccess('Innstillinger lagret');
      } else {
        const data = await response.json();
        setError(data.error || 'Kunne ikke lagre innstillinger');
      }
    } catch (err: any) {
      setError(err.message || 'Nettverksfeil');
    } finally {
      setSaving(false);
    }
  };

  // Handle CSV file upload
  const handleCSVUpload = async (type: 'skills' | 'occupations' | 'relations', file: File, language: string) => {
    setImporting(type);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('language', language);

      const response = await authenticatedFetch(`/api/esco/local/import/${type}`, {
        method: 'POST',
        body: formData,
        headers: {
          // Don't set Content-Type - let browser set it with boundary
        }
      });

      const data = await response.json();

      if (response.ok) {
        const label = type === 'skills' ? 'ferdigheter' : type === 'occupations' ? 'yrkesroller' : 'relasjoner';
        setSuccess(`Importert ${data.imported?.toLocaleString() || 0} ${label}. ${data.errors > 0 ? `(${data.errors} feil)` : ''}`);
        await fetchStatus();
        await fetchHistory();
      } else {
        setError(data.error || `Kunne ikke importere ${type}`);
      }
    } catch (err: any) {
      setError(err.message || 'Nettverksfeil under opplasting');
    } finally {
      setImporting(null);
    }
  };

  // Clear ESCO data
  const handleClearData = async (type: 'skills' | 'occupations' | 'all') => {
    const typeLabel = type === 'all' ? 'alle ESCO-data' : type === 'skills' ? 'alle ferdigheter' : 'alle yrkesroller';
    const confirmed = await confirm({
      title: 'Slett data',
      message: `Er du sikker på at du vil slette ${typeLabel}? Dette kan ikke angres.`,
      confirmLabel: 'Slett',
      cancelLabel: 'Avbryt',
      variant: 'danger'
    });

    if (!confirmed) return;

    setClearing(true);
    setError('');
    setSuccess('');

    try {
      const response = await authenticatedFetch(`/api/esco/local/clear?type=${type}`, {
        method: 'DELETE'
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(data.message || 'Data slettet');
        await fetchStatus();
      } else {
        setError(data.error || 'Kunne ikke slette data');
      }
    } catch (err: any) {
      setError(err.message || 'Nettverksfeil');
    } finally {
      setClearing(false);
    }
  };

  // Pre-cache relations via API
  const fetchPrecacheStatus = async () => {
    try {
      const res = await authenticatedFetch('/api/esco/local/relations/precache-status');
      if (res.ok) {
        const data = await res.json();
        setPrecacheState(data.state);
        return data.state;
      }
    } catch {}
    return null;
  };

  const handleStartPrecache = async (force = false) => {
    setError('');
    setSuccess('');
    try {
      const res = await authenticatedFetch('/api/esco/local/relations/precache-all', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ force })
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Kunne ikke starte pre-cache');
        return;
      }
      setPrecacheState(data.state);
      setPrecachePolling(true);
    } catch (e: any) {
      setError(e.message || 'Nettverksfeil');
    }
  };

  // Poll precache status while running
  useEffect(() => {
    if (!precachePolling) return;
    const interval = setInterval(async () => {
      const state = await fetchPrecacheStatus();
      if (state && !state.running) {
        setPrecachePolling(false);
        if (state.error) {
          setError(`Pre-cache feilet: ${state.error}`);
        } else if (state.result) {
          setSuccess(`Pre-cache fullført. ${state.result.fetched} yrker hentet, ${state.result.relations_added} relasjoner lagt til.`);
        }
        await fetchStatus();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [precachePolling]);

  // Initial load: check if pre-cache is already running
  useEffect(() => {
    fetchPrecacheStatus().then(state => {
      if (state?.running) setPrecachePolling(true);
    });
  }, []);

  const handleClearRelations = async () => {
    const confirmed = await confirm({
      title: 'Slett alle relasjoner',
      message: 'Er du sikker på at du vil slette alle yrke-ferdighet-relasjoner? Dette kan ikke angres.',
      confirmLabel: 'Slett',
      cancelLabel: 'Avbryt',
      variant: 'danger'
    });
    if (!confirmed) return;
    try {
      const res = await authenticatedFetch('/api/esco/local/relations', { method: 'DELETE' });
      const data = await res.json();
      if (res.ok) {
        setSuccess(`Slettet ${data.deleted?.toLocaleString() || 0} relasjoner`);
        await fetchStatus();
      } else {
        setError(data.error || 'Kunne ikke slette relasjoner');
      }
    } catch (e: any) {
      setError(e.message || 'Nettverksfeil');
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('nb-NO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (ms: number) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}min`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-kf-blue" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Database className="w-6 h-6 text-kf-blue" />
            ESCO Taksonomi
          </h2>
          <p className="text-sm text-kf-gray-600 mt-1">
            European Skills, Competences, Qualifications and Occupations
          </p>
        </div>
        <a
          href="https://esco.ec.europa.eu"
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-kf-blue hover:underline"
        >
          esco.ec.europa.eu
        </a>
      </div>

      {/* Status messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 p-4 rounded-lg flex items-center gap-2">
          <CheckCircle className="w-5 h-5" />
          {success}
        </div>
      )}

      {/* Statistics cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <BookOpen className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-kf-gray-600">Ferdigheter</p>
              <p className="text-2xl font-bold">{statistics.skills_count.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Briefcase className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-kf-gray-600">Yrkesroller</p>
              <p className="text-2xl font-bold">{statistics.occupations_count.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Calendar className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-kf-gray-600">Sist vellykkede oppdatering</p>
              <p className="text-lg font-semibold">
                {syncStatus?.last_successful_sync
                  ? formatDate(syncStatus.last_successful_sync.synced_at)
                  : 'Aldri'}
              </p>
              {syncStatus?.last_successful_sync && (
                <p className="text-xs text-kf-gray-500 mt-0.5">
                  via {syncStatus.last_successful_sync.sync_type === 'csv_import' ? 'CSV-import' : 'EU ESCO API'}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sync section */}
      <div className="bg-white border rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <RefreshCw className="w-5 h-5" />
          Synkronisering
        </h3>

        <div className="space-y-4">
          {/* Last sync info */}
          {syncStatus && (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-kf-gray-600">Versjon:</span>
                  <span className="ml-2 font-medium">{syncStatus.esco_version}</span>
                </div>
                <div>
                  <span className="text-kf-gray-600">Status:</span>
                  <span className={`ml-2 font-medium ${
                    syncStatus.status === 'completed' ? 'text-green-600' :
                    syncStatus.status === 'failed' ? 'text-red-600' :
                    syncStatus.status === 'running' ? 'text-blue-600' : ''
                  }`}>
                    {syncStatus.status === 'completed' ? 'Fullfort' :
                     syncStatus.status === 'failed' ? 'Feilet' :
                     syncStatus.status === 'running' ? 'Kjorer...' : syncStatus.status}
                  </span>
                </div>
                <div>
                  <span className="text-kf-gray-600">Varighet:</span>
                  <span className="ml-2 font-medium">{formatDuration(syncStatus.sync_duration_ms)}</span>
                </div>
                <div>
                  <span className="text-kf-gray-600">Ferdigheter:</span>
                  <span className="ml-2 font-medium">{syncStatus.skills_count?.toLocaleString() || 0}</span>
                </div>
              </div>
              {syncStatus.error_message && (
                <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-red-800 mb-1">Synkronisering feilet</p>
                  <p className="text-sm text-red-700 whitespace-pre-line">{syncStatus.error_message}</p>
                </div>
              )}
              {/* Sist vellykkede import — viser når DB faktisk ble oppdatert */}
              {syncStatus.last_successful_sync && syncStatus.status !== 'completed' && (
                <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-800">
                    <strong>Databasen ble sist vellykket oppdatert:</strong> {formatDate(syncStatus.last_successful_sync.synced_at)}
                    {' · '}{syncStatus.last_successful_sync.skills_count?.toLocaleString('nb-NO')} ferdigheter
                    {' · '}{syncStatus.last_successful_sync.occupations_count?.toLocaleString('nb-NO')} yrkesroller
                  </p>
                  <p className="text-xs text-green-700 mt-1">
                    Den feilede importen over påvirker IKKE eksisterende data — ESCO-matching fungerer normalt med dagens database.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Manual sync button */}
          <div className="space-y-3">
            <div className="flex items-center gap-4">
              <button
                onClick={handleManualSync}
                disabled={syncing}
                className="btn-primary flex items-center gap-2"
              >
                {syncing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Synkroniserer...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Start manuell synkronisering
                  </>
                )}
              </button>
              {!syncing && (
                <p className="text-sm text-kf-gray-600">
                  Laster ned ESCO-data fra EU. Dette kan ta flere minutter.
                </p>
              )}
            </div>

            {/* Progress indicator during sync */}
            {syncing && syncProgress && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Loader2 className="w-5 h-5 animate-spin text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-blue-800">{syncProgress}</p>
                    <p className="text-sm text-blue-700 mt-1">
                      <strong>Du kan trygt forlate denne siden.</strong> Synkroniseringen kjører i bakgrunnen på server.
                      Kom tilbake hit senere — fremdrift vises automatisk så lenge synken pågår.
                    </p>
                    <p className="text-xs text-blue-600 mt-2">
                      En full sync tar typisk 5–15 minutter avhengig av nettverk og DB-belastning.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CSV Import section */}
      <div className="bg-white border rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <FileSpreadsheet className="w-5 h-5" />
          CSV-import
        </h3>

        <div className="space-y-6">
          {/* Instructions */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h4 className="font-medium text-amber-800 mb-2">Slik laster du ned ESCO CSV-filer</h4>
            <ol className="text-sm text-amber-700 list-decimal list-inside space-y-1">
              <li>Gå til <a href="https://esco.ec.europa.eu/en/use-esco/download" target="_blank" rel="noopener noreferrer" className="underline hover:text-amber-900">esco.ec.europa.eu/en/use-esco/download</a></li>
              <li>Velg versjon (f.eks. v1.2.1), innholdstype (Skills/Occupations), språk og CSV-format</li>
              <li>Oppgi e-postadresse for å motta nedlastingslenke</li>
              <li>Last ned CSV-filene og importer dem her</li>
            </ol>
          </div>

          {/* Skills import */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <BookOpen className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-medium">Importer ferdigheter</h4>
                  <p className="text-sm text-kf-gray-600">Last opp skills CSV-fil fra ESCO</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <select
                id="skills-language"
                className="input w-32"
                defaultValue="en"
              >
                <option value="en">Engelsk</option>
                <option value="no">Norsk</option>
              </select>
              <label className="flex-1">
                <input
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    const lang = (document.getElementById('skills-language') as HTMLSelectElement)?.value || 'en';
                    if (file) handleCSVUpload('skills', file, lang);
                    e.target.value = ''; // Reset for same file
                  }}
                  disabled={!!importing}
                />
                <span className={`btn-secondary flex items-center gap-2 cursor-pointer ${importing === 'skills' ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  {importing === 'skills' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Importerer...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Velg CSV-fil
                    </>
                  )}
                </span>
              </label>
              <button
                onClick={() => handleClearData('skills')}
                disabled={clearing || !!importing}
                className="btn-danger-outline flex items-center gap-2"
                title="Slett alle ferdigheter"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Occupations import */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Briefcase className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-medium">Importer yrkesroller</h4>
                  <p className="text-sm text-kf-gray-600">Last opp occupations CSV-fil fra ESCO</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <select
                id="occupations-language"
                className="input w-32"
                defaultValue="en"
              >
                <option value="en">Engelsk</option>
                <option value="no">Norsk</option>
              </select>
              <label className="flex-1">
                <input
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    const lang = (document.getElementById('occupations-language') as HTMLSelectElement)?.value || 'en';
                    if (file) handleCSVUpload('occupations', file, lang);
                    e.target.value = '';
                  }}
                  disabled={!!importing}
                />
                <span className={`btn-secondary flex items-center gap-2 cursor-pointer ${importing === 'occupations' ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  {importing === 'occupations' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Importerer...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Velg CSV-fil
                    </>
                  )}
                </span>
              </label>
              <button
                onClick={() => handleClearData('occupations')}
                disabled={clearing || !!importing}
                className="btn-danger-outline flex items-center gap-2"
                title="Slett alle yrkesroller"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Yrke-ferdighet relasjoner */}
          <div className="border-2 border-purple-200 rounded-lg p-4 bg-purple-50">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <RefreshCw className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h4 className="font-medium">Yrke-ferdighet-relasjoner</h4>
                  <p className="text-sm text-kf-gray-600">
                    Essential + optional skills per yrke. Status: <strong>{(syncStatus?.relations_count ?? 0).toLocaleString()}</strong> relasjoner
                  </p>
                </div>
              </div>
            </div>

            {/* Pre-cache via API */}
            <div className="bg-white rounded p-3 mb-3 border border-purple-100">
              <div className="flex items-center justify-between mb-2">
                <strong className="text-sm">Primær: hent via ESCO API</strong>
                {!precacheState?.running && (
                  <button
                    onClick={() => handleStartPrecache(false)}
                    disabled={!!importing}
                    className="btn-primary text-sm flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Start pre-cache
                  </button>
                )}
              </div>
              <p className="text-xs text-kf-gray-600 mb-2">
                Henter relasjoner direkte fra ec.europa.eu/esco/api for alle yrker. Hopper over yrker som allerede har data. Tar ~5-10 min for full kjøring.
              </p>
              {precacheState?.running && (
                <div className="bg-blue-50 border border-blue-200 rounded p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                    <span className="font-medium text-sm text-blue-800">
                      Henter relasjoner... {precacheState.index} / {precacheState.total} ({((precacheState.index / Math.max(precacheState.total, 1)) * 100).toFixed(1)}%)
                    </span>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-2 mb-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all"
                      style={{ width: `${(precacheState.index / Math.max(precacheState.total, 1)) * 100}%` }}
                    />
                  </div>
                  <div className="text-xs text-blue-700">
                    Hentet: {precacheState.fetched} · Hoppet over: {precacheState.skipped} · Feil: {precacheState.errors} · Relasjoner lagt til: {precacheState.relationsAdded}
                  </div>
                </div>
              )}
              {!precacheState?.running && precacheState?.finishedAt && precacheState.result && (
                <div className="bg-green-50 border border-green-200 rounded p-2 text-xs text-green-700">
                  ✓ Siste kjøring: hentet {precacheState.result.fetched} yrker, la til {precacheState.result.relations_added} relasjoner.
                </div>
              )}
            </div>

            {/* CSV-backup */}
            <div className="bg-white rounded p-3 mb-3 border border-purple-100">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <strong className="text-sm">Backup: CSV-import</strong>
                  <p className="text-xs text-kf-gray-600">
                    occupationSkillRelations.csv (språk-uavhengig). Bruk hvis API svikter.
                  </p>
                </div>
                <label>
                  <input
                    type="file"
                    accept=".csv"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleCSVUpload('relations', file, '');
                      e.target.value = '';
                    }}
                    disabled={!!importing || precacheState?.running}
                  />
                  <span className={`btn-secondary text-sm flex items-center gap-2 cursor-pointer ${(importing === 'relations' || precacheState?.running) ? 'opacity-50 cursor-not-allowed' : ''}`}>
                    {importing === 'relations' ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Importerer...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        Last opp CSV
                      </>
                    )}
                  </span>
                </label>
              </div>
            </div>

            {/* Slett alle relasjoner */}
            <div className="flex justify-end">
              <button
                onClick={handleClearRelations}
                disabled={!!importing || precacheState?.running}
                className="btn-danger-outline text-sm flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Slett alle relasjoner
              </button>
            </div>
          </div>

          {/* Clear all data */}
          <div className="pt-4 border-t">
            <button
              onClick={() => handleClearData('all')}
              disabled={clearing || !!importing}
              className="btn-danger flex items-center gap-2"
            >
              {clearing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              Slett alle ESCO-data
            </button>
            <p className="text-sm text-kf-gray-600 mt-2">
              Slett alle data for å starte på nytt med nye CSV-filer.
            </p>
          </div>
        </div>
      </div>

      {/* Settings section */}
      <div className="bg-white border rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Settings className="w-5 h-5" />
          Innstillinger
        </h3>

        <div className="space-y-4">
          {/* Enable/disable sync */}
          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium">Automatisk synkronisering</label>
              <p className="text-sm text-kf-gray-600">
                Sjekk for oppdateringer automatisk
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.sync_enabled}
                onChange={(e) => setSettings({ ...settings, sync_enabled: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-kf-blue"></div>
            </label>
          </div>

          {/* Sync frequency */}
          <div>
            <label className="block font-medium mb-2">Synkroniseringsfrekvens</label>
            <select
              value={settings.sync_frequency_days}
              onChange={(e) => setSettings({ ...settings, sync_frequency_days: parseInt(e.target.value) })}
              className="input w-full max-w-xs"
            >
              <option value={7}>Ukentlig (anbefalt)</option>
              <option value={14}>Annenhver uke</option>
              <option value={30}>Manedlig</option>
              <option value={90}>Kvartalsvis</option>
            </select>
            <p className="text-sm text-kf-gray-600 mt-1">
              ESCO oppdateres sjelden (1-2 ganger per ar). Ukentlig sjekk er tilstrekkelig.
            </p>
          </div>

          {/* Save button */}
          <div className="pt-4">
            <button
              onClick={handleSaveSettings}
              disabled={saving}
              className="btn-primary flex items-center gap-2"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4" />
              )}
              Lagre innstillinger
            </button>
          </div>
        </div>
      </div>

      {/* Sync history */}
      {syncHistory.length > 0 && (
        <div className="bg-white border rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Synkroniseringslogg
          </h3>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3">Tidspunkt</th>
                  <th className="text-left py-2 px-3">Versjon</th>
                  <th className="text-left py-2 px-3">Status</th>
                  <th className="text-left py-2 px-3">Ferdigheter</th>
                  <th className="text-left py-2 px-3">Yrkesroller</th>
                  <th className="text-left py-2 px-3">Varighet</th>
                </tr>
              </thead>
              <tbody>
                {syncHistory.map((entry) => (
                  <tr key={entry.id} className="border-b last:border-b-0">
                    <td className="py-2 px-3">{formatDate(entry.synced_at)}</td>
                    <td className="py-2 px-3 font-mono text-xs">{entry.esco_version}</td>
                    <td className="py-2 px-3">
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        entry.status === 'completed' ? 'bg-green-100 text-green-700' :
                        entry.status === 'failed' ? 'bg-red-100 text-red-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {entry.status === 'completed' ? 'Fullfort' :
                         entry.status === 'failed' ? 'Feilet' : entry.status}
                      </span>
                    </td>
                    <td className="py-2 px-3">{entry.skills_count?.toLocaleString() || '-'}</td>
                    <td className="py-2 px-3">{entry.occupations_count?.toLocaleString() || '-'}</td>
                    <td className="py-2 px-3">{formatDuration(entry.sync_duration_ms)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Info box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-800 mb-2">Om ESCO</h4>
        <p className="text-sm text-blue-700">
          ESCO (European Skills, Competences, Qualifications and Occupations) er EUs
          flerspraklige klassifisering av ferdigheter, kompetanser og yrker. Lokal
          lagring gir raskere oppslag og bedre fuzzy-matching for norske laeringsutbytter.
        </p>
        <p className="text-sm text-blue-700 mt-2">
          Ferdigheter kobles automatisk til laeringsutbytter i studieplaner og moduler.
          Yrkesroller kobles til malgruppe og opptakskrav.
        </p>
      </div>
    </div>
  );
}
